<template>
  <q-card flat bordered>
    <div class="q-pa-md flex justify-between">
      <div class="q-my-auto items-center row q-gutter-x-sm">
        <span class="text-h6">All Subscriptions</span>
        <template v-if="unitCountDifference !== 0 && $q.screen.gt.sm">
          <q-chip icon="warning" color="deep-orange" dense text-color="white">
            Unit Count Mismatch
            <q-tooltip max-width="250px">
              The difference between contracted unit count and actual unit count is
              <b>{{ unitCountDifference }}</b
              >. Please contact support@hellodata.ai for assistance.
            </q-tooltip>
          </q-chip>
        </template>
      </div>
      <div class="q-gutter-x-sm">
        <template v-if="$q.screen.gt.sm">
          <q-toggle v-model="displayCancel" label="Include Canceled" dense />
          <q-btn id="invoice-status_download" flat no-caps class="q-my-auto" @click="exportCSV">
            <q-icon name="download" class="q-mr-sm" size="medium" />Download
          </q-btn>
        </template>
        <q-btn icon="refresh" round flat unelevated size="sm" @click="refreshChildSubscriptions" />
      </div>
    </div>
    <q-table
      dense
      flat
      bordered
      virtual-scroll
      :rows="allSubscriptions"
      :columns="columns"
      :rows-per-page-options="[0]"
      :virtual-scroll-item-size="15"
      :virtual-scroll-sticky-size-start="15"
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #header-cell="props">
        <q-th :props="props">
          {{ props.col.label }}
          <q-tooltip v-if="props.col.description" max-width="250px">
            <b>{{ props.col.label }}</b>
            <br />
            {{ props.col.description }}
          </q-tooltip>
        </q-th>
      </template>
      <template #body-cell-status="props">
        <q-td :props="props" style="height: 43px">
          <q-chip
            v-if="!props.row.parent"
            :ripple="false"
            square
            dense
            :class="{
              'text-grey': props.row.ended_at,
              'text-deep-orange': props.row.past_due && !props.row.ended_at,
              'text-green': !props.row.past_due && !props.row.ended_at
            }"
            outline
          >
            {{ props.value }}
            <q-tooltip v-if="props.row.past_due">This subscription is past due.</q-tooltip>
          </q-chip>
          <q-chip :ripple="false" square dense v-if="props.row.cancel_at" class="text-grey" outline>
            {{ `Cancels ${format(parseISO(props.row.cancel_at), 'MMM dd')}` }}
          </q-chip>
        </q-td>
      </template>
      <template #body-cell-property_search_response="props">
        <q-td :props="props">
          {{ props.value }}
          <q-tooltip v-if="props.row.property_search_response">
            {{ props.row.property_search_response.street_address }},
            {{ props.row.property_search_response.city }},
            {{ props.row.property_search_response.state }}
            {{ props.row.property_search_response.zip_code }}
          </q-tooltip>
        </q-td>
      </template>
      <template #body-cell-actions="props">
        <q-td :props="props">
          <template v-if="!props.row.parent && !props.row.ended_at">
            <q-btn
              v-if="props.row.cancel_at"
              round
              dense
              unelevated
              @click="validateKeepAccount(props.row)"
            >
              <q-tooltip>Keep Account</q-tooltip>
              <q-icon name="thumb_up" size="medium" />
            </q-btn>
            <q-btn v-else round dense unelevated @click="validateCloseAccount(props.row)">
              <q-tooltip class="bg-red">Close Account</q-tooltip>
              <q-icon name="delete" size="medium" color="red" />
            </q-btn>
          </template>
          <q-btn v-else-if="props.row.parent" round dense unelevated @click="openBilling">
            <q-tooltip>Open Billing</q-tooltip>
            <q-icon name="account_balance" size="medium" />
          </q-btn>
        </q-td>
      </template>
    </q-table>
    <q-dialog
      v-model="closeAccountDialogOpen"
      transition-duration="100"
      @before-hide="closeAccountDetails = null"
    >
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <span class="q-ml-sm">
            Are you sure you want to close
            <b
              >{{ closeAccountDetails?.customer_name }} ({{
                closeAccountDetails?.customer_email
              }})</b
            >
            at the end of the current period?
          </span>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            no-caps
            unelevated
            label="Close Account"
            color="red"
            @click="closeAccount"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog
      v-model="keepAccountDialogOpen"
      transition-duration="100"
      @before-hide="keepAccountDetails = null"
    >
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <span class="q-ml-sm">
            Are you sure you want to keep
            <b>{{ keepAccountDetails?.customer_name }} ({{ keepAccountDetails?.customer_email }})</b
            >?
          </span>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            no-caps
            unelevated
            label="Keep"
            color="primary"
            @click="keepAccount"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script lang="ts">
import type { QTableColumn } from 'quasar'
import { formatMoney, formatNumber } from '@/utils'
import { type Subscription } from 'common'
import { exportFile } from 'quasar'
import { parseISO, format } from 'date-fns'

export default {
  name: 'BillingView',
  components: {},
  props: {
    subscriptions: {
      type: Array<Subscription>,
      required: true
    },
    childSubscriptions: {
      type: Array<Subscription>,
      required: true
    }
  },
  data() {
    return {
      closeAccountDialogOpen: false,
      closeAccountDetails: null as Subscription | null,
      keepAccountDialogOpen: false,
      keepAccountDetails: null as Subscription | null,
      displayCancel: false
    }
  },
  computed: {
    unitCountDifference() {
      const parentTotal = this.subscriptions.find((_) => !_.per_seat)?.quantity
      const childQuantities = this.childSubscriptions
        .filter((_) => !_.ended_at)
        .map((_) => _.quantity)
        .filter((_) => _) as number[]
      const childTotal = childQuantities.reduce((s, a) => s + a, 0)
      if (parentTotal && childTotal) {
        return parentTotal - childTotal
      }
      return 0
    },
    allSubscriptions() {
      if (this.childSubscriptions.length) {
        return [
          ...this.subscriptions.map((_) => ({ ..._, parent: true })),
          ...this.childSubscriptions
            .filter((_) => !_.ended_at)
            .map((_) => ({ ..._, parent: false })),
          ...(this.displayCancel
            ? this.childSubscriptions
                .filter((_) => _.ended_at)
                .map((_) => ({ ..._, parent: false }))
            : [])
        ]
      }
      return []
    },
    columns() {
      return [
        {
          field: 'customer_name',
          name: 'customer_name',
          label: 'Name',
          sortable: true,
          align: 'left',
          style: 'max-width: 400px; overflow: hidden; text-overflow: ellipsis;',
          description:
            'The name associated with this account, typically representing the company or building.'
        },
        {
          field: 'customer_email',
          name: 'customer_email',
          label: 'Email',
          sortable: true,
          align: 'left',
          description:
            'The primary email address linked to this account, generally used for corporate or building communications.'
        },
        {
          field: (row: Subscription) => {
            if (row.property_search_response) {
              const { building_name, street_address } = row.property_search_response
              return building_name || street_address
            }
            return ''
          },
          name: 'property_search_response',
          label: 'Property',
          sortable: true,
          align: 'left',
          description:
            'Indicates the property restrictions for subscriptions. Per seat subscriptions have no restriction, while per unit subscriptions are limited to specific properties that access the tool.'
        },
        {
          field: 'quantity',
          name: 'quantity',
          label: 'Quantity',
          sortable: true,
          align: 'left',
          format: (val: number | null) => (val ? formatNumber(val) : ''),
          description: 'The total number of units subscribed to for this account.'
        },
        {
          field: 'unit_amount',
          name: 'unit_amount',
          label: 'Unit Price',
          sortable: true,
          align: 'left',
          format: (val: number | null) => (val ? formatMoney(val, 2) : ''),
          description: 'The dollar amount per quantity.'
        },
        {
          field: (row: Subscription) =>
            row.quantity !== null && row.unit_amount !== null
              ? row.quantity * row.unit_amount
              : null,
          name: 'price',
          label: 'Price',
          sortable: true,
          align: 'left',
          format: (val: number | null) => (val ? formatMoney(val, 2) : ''),
          description:
            'The total monthly subscription cost for this account, calculated based on the quantity and unit price.'
        },
        {
          field: (row: Subscription) =>
            row.ended_at ? 'Canceled' : row.past_due ? 'Past Due' : 'Active',
          name: 'status',
          label: 'Status',
          sortable: true,
          align: 'left',
          description:
            'The current payment status of the account, indicating whether the last invoice is paid or overdue.'
        },
        {
          field: 'created',
          format: (val: string) => {
            return format(parseISO(val), 'yyyy-MM-dd')
          },
          name: 'created',
          label: 'Created',
          sortable: true,
          align: 'left',
          description: 'The date this subscription was created.'
        },
        this.displayCancel
          ? {
              field: 'ended_at',
              format: (val: string | null) => {
                return val ? format(parseISO(val), 'yyyy-MM-dd') : ''
              },
              name: 'ended_at',
              label: 'Canceled',
              sortable: true,
              align: 'left',
              description: 'The date this subscription was canceled.'
            }
          : undefined,
        {
          name: 'actions',
          label: '',
          align: 'center'
        }
      ].filter((_) => _) as (QTableColumn & { description?: string })[]
    }
  },
  emits: ['refresh-subscriptions', 'open-billing', 'close-child-account', 'keep-child-account'],
  methods: {
    format,
    parseISO,
    exportCSV() {
      const today = new Date().toISOString().split('T')[0]
      const header = [
        'Name',
        'Email',
        'Property',
        'Quantity',
        'Unit Price',
        'Price',
        'Status',
        'Created'
      ]
      if (this.displayCancel) {
        header.push('Canceled')
      }
      const rows = this.allSubscriptions.map((row) => {
        const val = [
          row.customer_name,
          row.customer_email,
          row.property_search_response?.building_name ||
            row.property_search_response?.street_address ||
            '',
          row.quantity,
          row.unit_amount,
          !row.parent
            ? row.quantity !== null && row.unit_amount !== null
              ? row.quantity * row.unit_amount
              : null
            : '',
          !row.parent ? (row.ended_at ? 'Canceled' : row.past_due ? 'Past Due' : 'Active') : '',
          format(parseISO(row.created), 'yyyy-MM-dd')
        ]
        if (this.displayCancel) {
          val.push(row.ended_at ? format(parseISO(row.ended_at), 'yyyy-MM-dd') : '')
        }
        return val
      })
      exportFile(
        `hello-data-invoice-status-${today}.csv`,
        header.join(',') + '\n' + rows.map((row) => row.map((_) => `"${_}"`).join(',')).join('\n')
      )
    },
    refreshChildSubscriptions() {
      this.$emit('refresh-subscriptions')
    },
    openBilling() {
      this.$emit('open-billing')
    },
    validateKeepAccount(row: Subscription) {
      this.keepAccountDialogOpen = true
      this.keepAccountDetails = row
    },
    validateCloseAccount(row: Subscription) {
      this.closeAccountDialogOpen = true
      this.closeAccountDetails = row
    },
    closeAccount() {
      if (this.closeAccountDetails) {
        this.$emit('close-child-account', this.closeAccountDetails.id)
        this.closeAccountDetails = null
        this.closeAccountDialogOpen = false
      }
    },
    keepAccount() {
      if (this.keepAccountDetails) {
        this.$emit('keep-child-account', this.keepAccountDetails.id)
        this.keepAccountDetails = null
        this.keepAccountDialogOpen = false
      }
    }
  }
}
</script>
<style scoped>
.modal-title {
  font-size: 20px;
  font-weight: 400;
}
</style>
